// Закрытие официального периода с переносом остатка и переоткрытие с откатом переноса.
//
// ## Кнопка не сереет никогда
//
// Выключенная кнопка не доводит нажатие до обработчика: человек жмёт и не получает ничего, а со
// стороны это неотличимо от сломанной программы. Поэтому нажатие при незакрываемом периоде
// открывает разбор и НАЗЫВАЕТ, сколько траншей не проведено и на какую сумму (правило 10).
//
// ## Числа стоят в окне ДО нажатия
//
// Предпросмотр закрытия грузится вместе с сеткой, а не по нажатию: иначе окно открывается
// пустым и человек ждёт в нём ровно те цифры, ради которых его открыл. Правило 4 требует от
// подтверждения необратимого действия итоговую цифру и число строк, а не вопрос «вы уверены»:
// уверенность человека — не информация.
//
// ## После закрытия итог проговаривается словами
//
// «Перенесено 543 970,06 ₽ по 28 строкам на 28 человек» — иначе самое дорогое действие месяца
// выглядит как закрывшееся окно, и проверять его человек идёт в следующий период руками.

import { useState } from 'react';

import { RiLockLine } from '@remixicon/react';

import type {
  ClosePreviewOut,
  OfficialPeriodOut,
  ReopenPreviewOut,
} from '@/shared/api/endpoints/official';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, parseMoment } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

import { useClosePeriod, useReopenPeriod, useReopenPreview } from './queries';

const CLOSE_PERMISSION = 'core.official.close_period';
const REOPEN_PERMISSION = 'core.official.reopen_period';

export function ClosePeriodBar({
  период,
  предпросмотр,
  onOpenReview,
  onGoToPeriod,
}: {
  период: OfficialPeriodOut;
  /** Предпросмотр закрытия. Пусто — ещё едет; окно тогда честно говорит, что числа считаются. */
  предпросмотр: ClosePreviewOut | undefined;
  /** Переход в разбор замечаний: то, что предлагается вместо отказа. */
  onOpenReview: () => void;
  /** Перейти в период, куда ушёл остаток. */
  onGoToPeriod: (periodId: string) => void;
}) {
  const права = useAuthStore((state) => state.permissions);
  const закрытие = useClosePeriod(период.id);
  const переоткрытие = useReopenPeriod(период.id);
  const [открыто, setОткрыто] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);

  const мешает = предпросмотр?.blockers ?? [];
  const итог = закрытие.data;

  // Закрытие меняет период на «закрыт» и полоса перестраивается на переоткрытие. Окно с итогом
  // при этом обязано остаться: самое дорогое действие месяца не имеет права выглядеть как
  // просто закрывшееся окно — иначе проверять, ушли ли деньги, человек идёт в новый период
  // руками.
  // Переоткрытие снимает признак «закрыт» — и полоса мгновенно перестроилась бы на закрытие,
  // унеся единственное место, где написано, сколько денег откатилось. Поэтому пока отчёт о
  // переоткрытии не закрыт человеком, остаёмся в этой ветке: ровно та же причина, что и с итогом
  // закрытия абзацем выше.
  if ((период.status === 'closed' || переоткрытие.data !== undefined) && итог === undefined) {
    return <ПереоткрытьПериод период={период} переоткрытие={переоткрытие} />;
  }

  return (
    <div className='ml-auto flex flex-wrap items-center gap-2'>
      {запрет !== null && <span className='text-paragraph-xs text-text-sub-600'>{запрет}</span>}
      {период.status === 'closed' ? (
        <Alert.Root variant='lighter' status='information' size='xsmall'>
          <Alert.Icon as={RiLockLine} />
          Закрыт
        </Alert.Root>
      ) : (
        <Button.Root
          variant='primary'
          mode='stroke'
          size='xsmall'
          onClick={() => {
            if (!can(права, CLOSE_PERMISSION)) {
              setЗапрет(
                'Закрывает период финансовый отдел или администратор. Скопируйте ссылку на этот период и передайте им.',
              );
              return;
            }
            setЗапрет(null);
            setОткрыто(true);
          }}
        >
          Закрыть период
        </Button.Root>
      )}

      <Modal.Root open={открыто} onOpenChange={setОткрыто}>
        <Modal.Content className='max-w-lg'>
          <Modal.Header title={`Закрыть период ${период.title}?`} />
          <Modal.Body>
            <div className='flex flex-col gap-2 text-paragraph-sm text-text-sub-600'>
              {итог !== undefined ? (
                <ИтогЗакрытия итог={итог} onGoToPeriod={onGoToPeriod} />
              ) : предпросмотр === undefined ? (
                <p>Считаем, что перенесётся. Это занимает мгновение.</p>
              ) : мешает.length > 0 ? (
                <>
                  {/* Не «нельзя», а что именно мешает и что с этим сделать. */}
                  <p className='text-text-strong-950'>Закрыть пока нельзя, вот почему:</p>
                  <ul className='ml-4 list-disc'>
                    {мешает.map((причина) => (
                      <li key={причина}>{причина}</li>
                    ))}
                  </ul>
                  <p>
                    Заведённый транш в закрытом периоде провести будет нечем — сторож базы не даст,
                    и деньги повиснут заведёнными навсегда.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Строк в периоде {предпросмотр.rows_total}, погашено полностью{' '}
                    {предпросмотр.settled_rows}.
                  </p>
                  <p className='text-text-strong-950'>
                    Перенесётся {formatKopecks(предпросмотр.carry_total)} по{' '}
                    {countLabel(предпросмотр.carry_rows, ['строке', 'строкам', 'строкам'])} на{' '}
                    {countLabel(предпросмотр.carry_people, ['человека', 'человек', 'человек'])} в
                    период {предпросмотр.next_period_title}.
                  </p>
                  {предпросмотр.refused_rows > 0 && (
                    <p>
                      Отказов от переноса {предпросмотр.refused_rows} на{' '}
                      {formatKopecks(предпросмотр.refused_total)} — эти деньги в новый период не
                      уйдут.
                    </p>
                  )}
                  {предпросмотр.overpaid_rows > 0 && (
                    <p className='text-warning-base'>
                      Переплат {предпросмотр.overpaid_rows} на{' '}
                      {formatKopecks(предпросмотр.overpaid_total)} — они не переносятся, их
                      разбирают вручную.
                    </p>
                  )}
                  <p>
                    После закрытия транши в этом периоде не заводятся и не проводятся. Переоткрыть
                    может только администратор, и переоткрытие пишется в журнал.
                  </p>
                </>
              )}
              {закрытие.error !== null && <ErrorAlert error={закрытие.error as never} />}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='small'
              onClick={() => {
                setОткрыто(false);
                // Итог сбрасывается только по закрытию окна человеком: пока оно открыто,
                // числа переноса остаются перед глазами, сколько бы раз ни обновилась сетка.
                закрытие.reset();
              }}
            >
              {итог === undefined ? 'Отмена' : 'Закрыть окно'}
            </Button.Root>
            {итог === undefined &&
              (мешает.length > 0 ? (
                <Button.Root
                  variant='primary'
                  mode='filled'
                  size='small'
                  onClick={() => {
                    setОткрыто(false);
                    onOpenReview();
                  }}
                >
                  Разобрать
                </Button.Root>
              ) : (
                <Button.Root
                  variant='primary'
                  mode='filled'
                  size='small'
                  onClick={() => закрытие.mutate(период.version)}
                >
                  {закрытие.isPending ? 'Закрываем…' : 'Закрыть период'}
                </Button.Root>
              ))}
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
}

function ИтогЗакрытия({
  итог,
  onGoToPeriod,
}: {
  итог: {
    carried_total: number;
    carried_rows: number;
    carried_people: number;
    next_period_id: string;
    next_period_title: string;
  };
  onGoToPeriod: (periodId: string) => void;
}) {
  return (
    <>
      <p className='text-text-strong-950'>
        Период закрыт. Перенесено {formatKopecks(итог.carried_total)} по{' '}
        {countLabel(итог.carried_rows, ['строке', 'строкам', 'строкам'])} на{' '}
        {countLabel(итог.carried_people, ['человека', 'человек', 'человек'])} в период{' '}
        {итог.next_period_title}.
      </p>
      <p>
        В новом периоде эти деньги стоят отдельными строками без процента по исполнительному
        документу: удержание уже применили там, где деньги начислялись.
      </p>
      <Button.Root
        variant='primary'
        mode='stroke'
        size='xsmall'
        onClick={() => onGoToPeriod(итог.next_period_id)}
      >
        Перейти в период {итог.next_period_title}
      </Button.Root>
    </>
  );
}

function ПереоткрытьПериод({
  период,
  переоткрытие,
}: {
  период: OfficialPeriodOut;
  переоткрытие: ReturnType<typeof useReopenPeriod>;
}) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, REOPEN_PERMISSION);
  const предпросмотр = useReopenPreview(период.id, можно);
  const [основание, setОснование] = useState('');
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [открыто, setОткрыто] = useState(false);

  const когда = parseMoment(период.closed_at);
  const отчёт = переоткрытие.data;

  // Переоткрывать предлагается, только когда УЖЕ известно, что этому мешает. Пока предпросмотр
  // едет — и тем более если он не приехал вовсе, — ответа нет, а «ответа нет» это не
  // «препятствий нет». До 25.08.2026 здесь стояло `?? 0`: красная кнопка и поле основания
  // появлялись мгновенно, то есть самое дорогое действие месяца предлагалось раньше, чем
  // система узнавала, можно ли его делать.
  const можноПереоткрыть =
    отчёт === undefined &&
    предпросмотр.data !== undefined &&
    предпросмотр.data.blockers.length === 0;

  const закрытьОкно = () => {
    setОткрыто(false);
    // Отчёт держится, пока человек не закрыл окно сам: период к этому времени уже открыт, и
    // без этого строка «отменено переносом столько-то» исчезала бы в тот же миг, непрочитанной.
    переоткрытие.reset();
  };

  return (
    <div className='ml-auto flex flex-wrap items-center gap-2'>
      {период.status === 'closed' && (
        <Alert.Root variant='lighter' status='information' size='xsmall'>
          <Alert.Icon as={RiLockLine} />
          Закрыт
          {период.closed_by_name !== null && <> · {период.closed_by_name}</>}
          {когда !== null && <> · {formatDateDMY(когда)}</>}
        </Alert.Root>
      )}
      {можно ? (
        <>
          {период.status === 'closed' && (
            <Button.Root
              variant='error'
              mode='stroke'
              size='xsmall'
              onClick={() => setОткрыто(true)}
            >
              Переоткрыть
            </Button.Root>
          )}
          <Modal.Root
            open={открыто}
            onOpenChange={(открыт) => (открыт ? setОткрыто(true) : закрытьОкно())}
          >
            <Modal.Content className='max-w-lg'>
              <Modal.Header title={`Переоткрыть период ${период.title}?`} />
              <Modal.Body>
                <div className='flex flex-col gap-2 text-paragraph-sm text-text-sub-600'>
                  {отчёт === undefined && (
                    <ПредпросмотрПереоткрытия
                      данные={предпросмотр.data}
                      ошибка={предпросмотр.error}
                    />
                  )}
                  {можноПереоткрыть && (
                    <TextField
                      id='official-reopen-basis'
                      label='Основание переоткрытия'
                      required
                      value={основание}
                      onChange={setОснование}
                      placeholder='ошиблись в сумме по двум людям, правим до сдачи ведомости'
                      error={ошибка}
                    />
                  )}
                  {переоткрытие.error !== null && (
                    <ErrorAlert error={переоткрытие.error as never} />
                  )}
                  {отчёт !== undefined && (
                    <p className='text-text-strong-950'>
                      Период вернулся в работу. Отменено переносом{' '}
                      {formatKopecks(отчёт.reverted_total)} по{' '}
                      {countLabel(отчёт.reverted_rows, ['строке', 'строкам', 'строкам'])} в
                      следующем периоде.
                    </p>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button.Root variant='neutral' mode='stroke' size='small' onClick={закрытьОкно}>
                  {отчёт === undefined ? 'Отмена' : 'Закрыть окно'}
                </Button.Root>
                {можноПереоткрыть && (
                  <Button.Root
                    variant='error'
                    mode='filled'
                    size='small'
                    onClick={() => {
                      if (основание.trim().length < 3) {
                        setОшибка(
                          'Основание обязательно: без него в журнале останется откат переноса без единого слова о том, почему.',
                        );
                        return;
                      }
                      setОшибка(undefined);
                      переоткрытие.mutate({
                        version: период.version,
                        basis: основание.trim(),
                      });
                    }}
                  >
                    {переоткрытие.isPending ? 'Переоткрываем…' : 'Переоткрыть'}
                  </Button.Root>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Root>
        </>
      ) : (
        <span className='max-w-72 text-paragraph-xs text-text-sub-600'>
          Переоткрывает администратор. Скопируйте ссылку на этот период из адресной строки и
          передайте ему вместе с причиной.
        </span>
      )}
    </div>
  );
}

function ПредпросмотрПереоткрытия({
  данные,
  ошибка,
}: {
  данные: ReopenPreviewOut | undefined;
  ошибка: unknown;
}) {
  // Отдельная ветка, а не молчание: без неё окно вечно писало «считаем», а красная кнопка при
  // этом ждала нажатия — то есть предлагала откатить перенос вслепую.
  if (ошибка !== null && ошибка !== undefined) {
    return (
      <>
        <p className='text-text-strong-950'>Не удалось выяснить, что вернётся при переоткрытии.</p>
        <ErrorAlert error={ошибка as never} />
        <p>
          Пока это неизвестно, переоткрывать нельзя: откат тронул бы деньги, о которых мы сейчас
          ничего не знаем. Обновите экран и попробуйте снова.
        </p>
      </>
    );
  }
  if (данные === undefined) {
    return <p>Считаем, что вернётся. Это занимает мгновение.</p>;
  }
  if (данные.blockers.length > 0) {
    return (
      <>
        <p className='text-text-strong-950'>Переоткрыть нельзя, вот почему:</p>
        <ul className='ml-4 list-disc'>
          {данные.blockers.map((причина) => (
            <li key={причина}>{причина}</li>
          ))}
        </ul>
        {данные.paid_people.length > 0 && (
          <p>
            По перенесённым строкам уже выплачивали: {данные.paid_people.join(', ')} — всего{' '}
            {formatKopecks(данные.paid_total)}. Выданные деньги не откатываются ничем, а молчаливый
            откат превратил бы выплату в фантом.
          </p>
        )}
        <p>
          Если суммы всё-таки неверны, снимите их сторно в том периоде, куда они перенеслись, — с
          основанием, как любую денежную запись.
        </p>
      </>
    );
  }
  return (
    <>
      <p className='text-text-strong-950'>
        Вернётся {formatKopecks(данные.carried_total)} по{' '}
        {countLabel(данные.carried_rows, ['строке', 'строкам', 'строкам'])}.
      </p>
      <p>
        Откат идёт сторно, а не удалением: транш переноса гасится обратной записью, строка переноса
        в следующем периоде обнуляется и получает пометку с вашим основанием.
      </p>
    </>
  );
}
